import * as signalR from '@microsoft/signalr'
import type { EnumTaskStatus } from '@/enums/task'

export interface IWSTask {
  failMsg: string
  status: EnumTaskStatus
  taskId: number
}

type TaskEventHandler = (payload: IWSTask) => void

export class SignalService {
  private connection: signalR.HubConnection | null = null
  private listenersBound = false

  private readonly onConnectionClosed = (error?: Error) => {
    console.log('connectionInfo', `连接已关闭: ${error ? error.message : '未知错误'}`)
  }

  constructor(
    private url: string,
    private token: string,
  ) {}

  //开始连接
  connect(): void {
    const connection = this.getOrCreateConnection()

    if (!this.listenersBound) {
      this.setupEventListeners(connection)
      this.listenersBound = true
    }

    const HUB_CONNECTION_STATE = signalR.HubConnectionState
    const resolveStatus = [
      HUB_CONNECTION_STATE.Connected,
      HUB_CONNECTION_STATE.Connecting,
      HUB_CONNECTION_STATE.Reconnecting,
    ]

    if (resolveStatus.includes(connection.state)) return

    connection.start().catch(err => {
      console.log(`Error connecting to SignalR: ${err}`)
    })
  }

  private getOrCreateConnection(): signalR.HubConnection {
    if (!this.connection) {
      const connection = new signalR.HubConnectionBuilder()
        .withUrl(this.url, {
          accessTokenFactory: () => this.token,
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000, 60000, 120000])
        .configureLogging(signalR.LogLevel.Information)
        .build()
      connection.serverTimeoutInMilliseconds = 30000 // 30秒未收到消息超时
      connection.keepAliveIntervalInMilliseconds = 15000 // 15秒发送一次
      this.connection = connection
    }

    return this.connection
  }

  private setupEventListeners(connection: signalR.HubConnection): void {
    connection.onclose(this.onConnectionClosed)
  }

  on(eventName: string, handler: TaskEventHandler) {
    const connection = this.getOrCreateConnection()
    connection.on(eventName, handler)

    return () => {
      connection.off(eventName, handler)
    }
  }

  // 销毁连接实例
  destroy(): void {
    if (!this.connection) {
      return
    }

    this.listenersBound = false

    const connection = this.connection
    this.connection = null

    if (connection.state !== signalR.HubConnectionState.Disconnected) {
      connection.stop().catch(err => {
        console.log(`Error stopping SignalR: ${err}`)
      })
    }
  }
}
